import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, FileDown } from "lucide-react";
import api, { apiErrorMessage, API_BASE } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import BrandMark from "@/components/common/BrandMark";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ProductThumb } from "@/components/common/ProductThumb";

export default function PublicQuote() {
  const { token } = useParams();
  const { t, fmtCurrency, fmtDate, locale, setLocale } = useI18n();
  const [data, setData] = useState(undefined);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get(`/public/quotes/${token}`, { _skipAuthRedirect: true }).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const decide = async (decision) => {
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/public/quotes/${token}/decision`, { accepted_by_name: name, decision }, { _skipAuthRedirect: true });
      setResult(r);
    } catch (e) { setResult({ error: apiErrorMessage(e, t) }); } finally { setBusy(false); }
  };

  if (data === undefined) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (data === null) return <div className="flex min-h-screen items-center justify-center p-6"><p className="text-slate-500" data-testid="public-not-found">{t("errors.not_found")}</p></div>;

  const q = data.quote;
  const decided = q.status !== "sent" || result;

  return (
    <div className="min-h-screen bg-slate-100 py-8 dark:bg-slate-950">
      <div className="mx-auto max-w-3xl px-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandMark className="h-9 w-9" />
            <span className="font-heading text-lg font-bold">Mayr-Technologies</span>
          </div>
          <button onClick={() => setLocale(locale === "de" ? "en" : "de")} className="text-xs font-medium uppercase text-slate-500">{locale === "de" ? "EN" : "DE"}</button>
        </div>

        <Card className="p-6" data-testid="public-quote-card">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold">{t("quotes.public_title", { number: q.quote_number })}</h1>
              <p className="mt-1 text-sm text-slate-500">{t("quotes.public_from")} {data.seller.legal_name || data.seller.name}</p>
            </div>
            <Button variant="outline" size="sm" onClick={async () => { const r = await api.get(`/public/quotes/${token}/pdf`, { responseType: "blob", _skipAuthRedirect: true }); window.open(URL.createObjectURL(r.data), "_blank"); }} data-testid="public-pdf-button"><FileDown className="h-4 w-4" />{t("quotes.download_pdf")}</Button>
          </div>

          <div className="mt-4 text-sm text-slate-600 dark:text-slate-400">
            <p>{data.customer_name}</p>
            {q.valid_until && <p className="mt-1">{t("quotes.valid_until")}: {fmtDate(q.valid_until)}</p>}
          </div>

          <div className="mt-6 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead className="w-12"></TableHead><TableHead>{t("quotes.description")}</TableHead><TableHead className="text-right">{t("quotes.quantity")}</TableHead><TableHead className="text-right">{t("quotes.unit_price")}</TableHead><TableHead className="text-right">{t("quotes.line_net")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {q.line_items.map((l) => (
                  <TableRow key={l.id} className={l.is_optional ? "opacity-60" : ""}>
                    <TableCell><ProductThumb path={l.image_url} alt={l.description} size={38} /></TableCell>
                    <TableCell className="font-medium">{l.description}{l.is_optional && <span className="ml-2 text-xs text-slate-400">({t("common.optional")})</span>}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.quantity} {l.unit}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtCurrency(l.unit_price)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtCurrency(l.line_net)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex justify-end">
            <div className="w-full max-w-xs space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">{t("quotes.subtotal")}</span><span className="tabular-nums">{fmtCurrency(q.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{t("quotes.tax_total")}</span><span className="tabular-nums">{fmtCurrency(q.tax_total)}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-1 font-heading text-base font-bold dark:border-slate-700"><span>{t("quotes.total")}</span><span className="tabular-nums" data-testid="public-total">{fmtCurrency(q.total)}</span></div>
            </div>
          </div>
        </Card>

        {result?.error ? (
          <p className="mt-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-300" data-testid="public-error">{result.error}</p>
        ) : (result?.status === "accepted" || q.status === "accepted") ? (
          <div className="mt-4 rounded-md bg-emerald-50 p-6 text-center dark:bg-emerald-950" data-testid="public-accepted"><CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" /><p className="mt-2 text-sm text-emerald-800 dark:text-emerald-300">{t("quotes.public_accepted")}</p></div>
        ) : (result?.status === "rejected" || q.status === "rejected") ? (
          <div className="mt-4 rounded-md bg-red-50 p-6 text-center dark:bg-red-950" data-testid="public-rejected"><XCircle className="mx-auto h-8 w-8 text-red-600" /><p className="mt-2 text-sm text-red-800 dark:text-red-300">{t("quotes.public_rejected")}</p></div>
        ) : decided && q.status !== "sent" ? (
          <p className="mt-4 text-center text-sm text-slate-500" data-testid="public-decided">{t("quotes.public_decided")}</p>
        ) : (
          <Card className="mt-4 p-6" data-testid="public-decision-form">
            <Label>{t("quotes.public_name_label")}</Label>
            <Input className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="public-name-input" />
            <div className="mt-4 flex gap-3">
              <Button className="flex-1" disabled={busy || name.trim().length < 2} onClick={() => decide("accept")} data-testid="public-accept-button"><CheckCircle2 className="h-4 w-4" />{t("quotes.public_accept")}</Button>
              <Button variant="outline" className="flex-1" disabled={busy || name.trim().length < 2} onClick={() => decide("reject")} data-testid="public-reject-button"><XCircle className="h-4 w-4" />{t("quotes.public_reject")}</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
